import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ThemeToggle from '../components/ThemeToggle';

const TenantLoginPage = () => {
  const { tenantSlug } = useParams();
  const { tenant, branding, loading: tenantLoading, error: tenantError } = useTenant();
  
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Get branding colors with fallbacks
  const primaryColor = branding?.primary_color || '#2563EB';
  const companyName = tenant?.name || 'Sales Portal';
  const logoUrl = branding?.logo_url;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let userData;
      if (isLogin) {
        userData = await login(email, password);
      } else {
        userData = await register(email, password, name);
      }
      
      // Redirect based on user role
      if (userData?.role_id === 'role_customer') {
        navigate('/portal');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (tenantError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Company Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">The company "{tenantSlug}" doesn't exist.</p>
          <Link to="/login" className="text-blue-600 hover:underline">Go to main login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4 transition-colors duration-300">
      {/* Theme Toggle - Top Right */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      
      <div className="w-full max-w-md">
        {/* Company Logo/Branding */}
        <div className="flex flex-col items-center justify-center mb-8">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt={companyName}
              className="h-16 object-contain mb-3"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          {/* Fallback logo */}
          <div 
            className={`${logoUrl ? 'hidden' : 'flex'} w-14 h-14 rounded-xl items-center justify-center mb-3`}
            style={{ backgroundColor: primaryColor }}
          >
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <span className="text-2xl font-bold text-gray-900 dark:text-white text-center">
            {companyName}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Sales Portal
          </span>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl dark:shadow-gray-900/50 p-8 transition-colors duration-300">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2 text-center">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-center mb-6">
            {isLogin ? 'Sign in to access your dashboard' : 'Get started with your account'}
          </p>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="dark:text-gray-300">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    className="pl-10 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="dark:text-gray-300">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  required
                  data-testid="tenant-login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="dark:text-gray-300">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  required
                  data-testid="tenant-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full text-white"
              style={{ backgroundColor: primaryColor }}
              disabled={loading}
              data-testid="tenant-login-submit"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm hover:underline"
              style={{ color: primaryColor }}
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>

        {/* Back to Configurator Link */}
        <div className="mt-6 text-center">
          <Link 
            to={`/c/${tenantSlug}`}
            className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
          >
            ← Back to {companyName} Configurator
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TenantLoginPage;
